import {Component, effect, Inject, Signal, TrackByFunction, ViewChild} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {Airline, Mutation, StartWorkflowVodInput} from '../../../types-workflow';

import {START_WORKFLOW_VOD_DEFAULT} from '../../../queries-workflow';
import {forkJoin} from 'rxjs';
import {Router} from '@angular/router';
import {VodDefaultFormatSpecificComponent} from '../vod-default-format-specific/vod-default-format-specific.component';
import {EncodingLadderComponent} from '../encoding-ladder/encoding-ladder.component';
import {normaliseLadder} from '../encoding-ladder/encoding-ladder.component';
import {stableStringify} from '../encoding-ladder/encoding-ladder.model';
import {BasicPoitemVodInfoComponent} from '../basics-poitem-vod-info/basic-poitem-vod-info.component';
import {clean_datetime} from '../../utils';
import {PO_DESTINATIONS, PoDestination} from '../../../target_systems';
import {Organization} from '../../../types-repository';
import {Po2024Service} from '../../../services/po-2024.service';
import {VodEncodeSettings, VodEncodeSettingsService} from '../../../services/vod-encode-settings.service';

import {MessageFieldComponent} from '../../do-button/message-field.component';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {ContextService} from '../../../services/context.service';
import {ProcessService} from '../../../services/process.service';
import {Dialog} from '@angular/cdk/dialog';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import { clFormFieldFocusFirstInvalid, clScrollToId } from '@castlabs/ui';



@Component({
  selector: 'app-vod-default-po',
  templateUrl: './vod-default-po.component.html',
  styleUrls: ['./vod-default-po.component.css'],
  imports: [
    ReactiveFormsModule,
    BasicPoitemVodInfoComponent,
    VodDefaultFormatSpecificComponent,
    EncodingLadderComponent,
    MessageFieldComponent,
    DoButtonComponent
]
})
export class VodDefaultPoComponent  {

  organization: Signal<Organization>;
  airlines: Signal<Airline[]>;
  poDestinations: PoDestination[] = []
  poFormGroup: FormGroup;

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  /** Import and export feedback. Separate from the submit button's field, next to its own buttons. */
  @ViewChild('settingsMessages') settingsMessages: MessageFieldComponent;

  /** Set while the form is being written to on purpose, so the write is not stored as an edit. */
  private restoringSettings = false;

  constructor(
    private apollo: Apollo,
    @Inject('vod_default') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    private banner: BannerService,
    contextService: ContextService,
    private processService: ProcessService,
    private router: Router,
    private dialog: Dialog,
    private encodeSettings: VodEncodeSettingsService,
  ) {
    this.organization = contextService.organization;
    this.airlines = contextService.airlines;

    // The output options as this operator last left them, or the defaults on a first visit. Read
    // once and used for every seed below, so the two controls cannot disagree about where their
    // starting value came from.
    const settings = this.encodeSettings.loadOrDefaults();

    this.poFormGroup = formBuilder.group({
      airline: [null, Validators.required],
      po_number: [clean_datetime(new Date()), Validators.required],
      po_destination: [null, Validators.required],
      auto_publish: [true, Validators.required],
      // Watermark, encryption and the ladder are one setting for the whole PO -- see the template
      // for why none of these is per item. Both are seeded from the stored settings rather than
      // from the defaults: they are how this operator encodes, which does not change between one
      // PO and the next, and re-picking a ladder every visit is what the persistence removes.
      format_specific_data: [settings.format_specific_data],
      // Never null: the ladder card always shows one of its presets selected, and a null here
      // would leave the submitted PO with no ladder at all while the card claimed otherwise. The
      // two are the same thing either way -- the default preset renders exactly what the template
      // renders with no key -- but only one of them says so out loud, and the stored PO is the
      // only record of what ran.
      encoding_ladder: [settings.encoding_ladder],
      poItems: formBuilder.array([]),
    });


    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        // The saved state is whatever shape the form had when it was stored, and the item shape has
        // changed (per-item format_specific_data and `thumbs` became PO-wide settings).
        // A saved item still carrying the old shape would build a FormGroup the template cannot
        // bind, and Angular would throw "Cannot find control with name: ..." on render. The
        // state is in-memory convenience, so normalise it rather than letting it break the page.
        // A state saved while `thumbs` was per item carries it on every entry, and the item form
        // has no such control any more. Dropping it silently would discard a choice the operator
        // made, so it is lifted to the PO instead: if any item asked for thumbnails, the PO does.
        // Items disagreeing is exactly the situation this move set out to remove.
        const savedThumbs = (savedForm.poItems ?? []).some((item: any) => item?.thumbs === true);
        savedForm = {
          ...savedForm,
          // Completed from the stored settings rather than the bare defaults, for the same reason
          // the controls are seeded from them: a key the saved state predates should fall back to
          // what this operator last chose, not to what a first-time visitor would see.
          format_specific_data: {
            ...settings.format_specific_data,
            ...(savedForm.format_specific_data ?? {}),
            ...(savedThumbs ? {thumbs: true} : {}),
          },
          poItems: (savedForm.poItems ?? []).map(({format_specific_data, thumbs, ...item}: any) => item),
        };
        savedForm.poItems.forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(poItem)));
        // setValue demands every control, so a form state saved before the ladder existed would
        // throw. Seed the new control's default and let the saved value win where it has one.
        this.poFormGroup.setValue(
          {
            format_specific_data: settings.format_specific_data,
            encoding_ladder: settings.encoding_ladder,
            ...savedForm,
          },
          {emitEvent: false})
      }
    });

    // Registered after the restore above, which patches with emitEvent: false and would otherwise
    // write back what it just read. Every later change to either card is stored as it happens:
    // there is no Save button, because a settings card nobody remembers to save is a settings card
    // that silently encodes last month's ladder.
    this.poFormGroup.controls['format_specific_data'].valueChanges.subscribe(() => this._persistSettings());
    this.poFormGroup.controls['encoding_ladder'].valueChanges.subscribe(() => this._persistSettings());

    effect(() => {
      this.poFormGroup.controls['airline'].valueChanges.subscribe(airline => {
        this.poDestinations = PO_DESTINATIONS.hasOwnProperty(this.organization().legacy_name)
          ? [{display_name: this.organization().legacy_name, destination_id: this.organization().legacy_name}, {
            display_name: 'Default CDN',
            destination_id: 'vod'
          }]
          : [{display_name: 'Default CDN', destination_id: 'vod'}];
        this.poFormGroup.controls['po_destination'].setValue(this.poDestinations[0].destination_id)
      })
    });
    this.poService.poItemToAdd$.subscribe(data => {
      console.log('poItemToAdd$')
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(
          Object.assign(
            {},
            // Nothing per item any more: an item is its basics and nothing else. `thumbs` was the
            // last per-item output option and is now PO-wide, in the format-specific card.
            {basics: BasicPoitemVodInfoComponent.DEFAULTS},
            data)))
      }
    });
    this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    });
    effect(() => {
      if (this.airlines().length === 1) {
        this.poFormGroup.controls['airline'].setValue(this.airlines()[0].iata_code);
      }
    })
  }



  compare_destination_id(o1: any, o2: any) {
    console.log(o1, o2)
    return o1?.destination_id === o2?.destination_id;
  }

  submitted = false;

  submit_po(button: DoButtonComponent) {
    try {
      this._submit_po(button)
    } catch (err) {
      // performAction() starts the spinner before emitting, so anything that throws on the way to
      // subscribe() leaves it spinning. Report it instead of stranding the button.
      console.error('submit_po failed', err)
      button.done(`Could not start the encode: ${err}`, {severity: 'cl-alert-danger'})
    }
  }

  private _submit_po(button: DoButtonComponent) {
    const organization = this.organization()
    if (!this.poFormGroup.valid) {
      this.submitted = true;
      const poItems = this.poFormGroup.controls.poItems as FormArray
      const invalidIdx = poItems.controls.findIndex(c => c.invalid)
      if (invalidIdx >= 0) {
        clScrollToId(`po-item-${invalidIdx}`, true);
        clFormFieldFocusFirstInvalid(`po-item-${invalidIdx}`, ['.ng-invalid']);
      } else {
        clFormFieldFocusFirstInvalid('po-form', ['.ng-invalid']);
      }

      button.done();
      return
    }
    const workflows = []
    for (const poItem of this.poFormGroup.value.poItems) {
      workflows.push(this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_VOD_DEFAULT,
        variables: {
          input_brefix: poItem.basics.input_brefix,
          po_item_id: poItem.basics.po_item_id.trim(),
          po_name: `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number.trim()}`,
          po_destination: this.poFormGroup.value.po_destination,
          // The full name, not the 'cmaf-abr' shorthand: default_generate_vtk_script prefixes
          // anything without a 'vod-default-' prefix, and the shorthand resolved to v1 -- which
          // has no encoding_ladder at all and silently ignores the key, since top-level
          // format_specific_data keys are not validated. v2 is the one that reads the ladder.
          vtk_template: 'vod-default-cmaf-abr-v2',
          auto_publish: this.poFormGroup.value.auto_publish,
          // Normalize here rather than trusting the control's contents: a restored form state
          // never passes through the child's emit path (see VodDefaultFormatSpecificComponent.forSubmit).
          // One PO-wide watermark/encryption/ladder/thumbnail setting, shared by every item.
          // The ladder is normalised for the same reason: the card's writeValue does not emit, so
          // an untouched one leaves the raw preset in the control -- and a preset states only what
          // it changes, which left gop_in_seconds/gops_per_segment out of the submitted job.
          format_specific_data: JSON.stringify(VodDefaultFormatSpecificComponent.forSubmit(
            this.poFormGroup.value.format_specific_data,
            normaliseLadder(this.poFormGroup.value.encoding_ladder)))
        } satisfies StartWorkflowVodInput

      }).pipe(
        map(d => ({
          process: d.data.start_workflow_vod_default,
          po_item_id: poItem.basics.po_item_id.trim()
        }))))
    }

    // forkJoin over an empty array completes without ever emitting, so `next` -- and with it
    // button.done() -- would never run: the button would spin forever with no request, no error
    // and nothing in the console. Nothing else guards this; the button has no disabled state.
    if (!workflows.length) {
      button.done('Add at least one PO item before starting an encode.', {severity: 'cl-alert-warning'});
      return
    }

    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('');

          // Read before the reset below, which is what clears the form.
          const kept = this._currentSettings();

          const ref = this.dialog.open(ConfirmDialogComponent, {
            data: {
              headline: 'Encoding started',
              message: `All encode${wfs.length === 1 ? '' : 's'} (${wfs.length} item${wfs.length === 1 ? '' : 's'}) started successfully.`,
            }
          });

          ref.closed.subscribe(() => {
            this.submitted = false;
            (this.poFormGroup.controls.poItems as FormArray).clear();
            this.poFormGroup.reset({
              airline: this.airlines().length === 1 ? this.airlines()[0].iata_code : null,
              po_number: clean_datetime(new Date()),
              po_destination: this.poFormGroup.value.po_destination,
              auto_publish: true,
              // Listed explicitly: reset() nulls any control it is not given, which would hand the
              // format-specific component a null value and drop the ladder. Carried over rather
              // than reset to the defaults -- the items are what a submitted PO consumes, the
              // output options are how this operator encodes, and the next PO wants them again.
              format_specific_data: kept.format_specific_data,
              encoding_ladder: kept.encoding_ladder,
              poItems: [],
            });
          });

          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} encoded.`,
              failureMessage: `Failed to encode ${workflow.po_item_id}.`,
              link: ['/#/o', organization.id, 'workflows',
                this.poFormGroup.value.airline,
                `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number}`].join('/')
            })
          })
        },
        error: err => {
          button.done('Workflow submission failed: ' + err, {severity: 'cl-alert-danger'});
        }
      }
    )

  }

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  // ── Encode settings: persistence, export, import ───────────────────────────

  /** Whether "Forget saved settings" has anything to forget. */
  get hasStoredSettings(): boolean {
    return this.encodeSettings.hasStored();
  }

  /** Write the current output options to a JSON file the operator keeps or shares. */
  exportSettings(): void {
    const now = new Date();
    const blob = new Blob([VodEncodeSettingsService.toFile(this._currentSettings(), now)],
      {type: 'application/json'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = VodEncodeSettingsService.fileName(now);
    a.click();
    window.URL.revokeObjectURL(url);
    this._settingsMessage('Encode settings exported.', 'cl-alert-info');
  }

  /**
   * Read a settings file back into the two cards.
   *
   * The file input is reset afterwards so picking the same file twice still fires a change event --
   * an import that silently does nothing the second time is worse than no import at all.
   */
  importSettings(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    file.text()
      .then(text => {
        this._applySettings(VodEncodeSettingsService.fromFile(text));
        this._settingsMessage(`Encode settings loaded from ${file.name}.`, 'cl-alert-info');
      })
      .catch(err => this._settingsMessage(`Could not import ${file.name}: ${err.message ?? err}`, 'cl-alert-danger'));
  }

  /**
   * Back to the defaults, and stop restoring anything on the next visit.
   *
   * The stored entry is removed rather than overwritten with the defaults, so a later version whose
   * defaults have moved on is not pinned to today's by an operator who once pressed this. The
   * subsequent valueChanges would store them again -- which is why `_persistSettings` is skipped
   * while this runs.
   */
  resetSettings(): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: 'Forget saved encode settings',
        message: 'The watermark, encryption and ladder settings go back to their defaults, and '
          + 'nothing is restored the next time you open this page. PO items are untouched.',
        actionName: 'Forget',
        action: () => {
          this.restoringSettings = true;
          this._applySettings(VodEncodeSettingsService.defaults());
          this.encodeSettings.clear();
          this.restoringSettings = false;
          this._settingsMessage('Saved encode settings forgotten.', 'cl-alert-info');
        },
      },
    });
  }

  /** The two PO-wide output settings as they currently stand in the form. */
  private _currentSettings(): VodEncodeSettings {
    return {
      format_specific_data: this.poFormGroup.controls['format_specific_data'].value,
      encoding_ladder: this.poFormGroup.controls['encoding_ladder'].value,
    };
  }

  /**
   * Write settings into the two cards.
   *
   * `setValue` per control rather than `patchValue` on the group: each card is a
   * ControlValueAccessor, and writing the control is what runs its `writeValue` and redraws it.
   * The events it emits are what stores the imported settings, so an import survives a reload the
   * same way a hand edit does.
   */
  private _applySettings(settings: VodEncodeSettings): void {
    this.poFormGroup.controls['format_specific_data'].setValue(settings.format_specific_data);
    this.poFormGroup.controls['encoding_ladder'].setValue(settings.encoding_ladder);
  }

  /**
   * Store the settings, unless there is nothing to store yet.
   *
   * The format-specific card emits once from its own ngOnInit, so simply saving on every event
   * would write an entry on a first visit where the operator has changed nothing -- pinning them
   * to today's defaults, which is the one thing "Forget saved settings" exists to undo. So while
   * nothing is stored, a value equal to the defaults is not stored either. Once something is
   * stored, every later change is, including one that lands back on the defaults: that is a choice
   * rather than an absence of one.
   *
   * Compared after `sanitise`, because the emitted value is not the defaults object -- the card
   * emits what the API receives, which drops the keys that only apply to a watermarked item.
   */
  private _persistSettings(): void {
    if (this.restoringSettings) {
      return;
    }
    const settings = this._currentSettings();
    if (!this.encodeSettings.hasStored()
      && stableStringify(VodEncodeSettingsService.sanitise(settings))
        === stableStringify(VodEncodeSettingsService.sanitise(VodEncodeSettingsService.defaults()))) {
      return;
    }
    this.encodeSettings.save(settings);
  }

  private _settingsMessage(message: string, severity: 'cl-alert-danger' | 'cl-alert-warning' | 'cl-alert-info'): void {
    this.settingsMessages?.setMessage(message, severity, severity === 'cl-alert-danger' ? -1 : 5000);
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.value)
  }

}
